import { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import Dashboard from './Dashboard';
import ClientView from './ClientView';
import OwnerDashboard from './OwnerDashboard';
import { isPlatformHost, applyBrandToDocument, KLINIO_DEFAULT_ACCENT } from './BrandContext';
import { NEUTRAL, STATUS_COLORS, onAccent } from './designTokens';
import './App.css';

// ─── Översätt råa Supabase-fel till mänskliga svenska meddelanden ─────────────
function authError(raw = '') {
  const msg = raw.toLowerCase();

  if (msg.includes('rate limit') || msg.includes('too many') || msg.includes('email rate'))
    return 'Du har gjort för många försök. Vänta några minuter och försök igen.';

  if (msg.includes('invalid login credentials') || msg.includes('invalid credentials'))
    return 'Fel e-postadress eller lösenord. Kontrollera att du skrivit rätt.';

  if (msg.includes('email not confirmed'))
    return 'E-postadressen är inte verifierad. Kolla din inkorg (och skräppostmappen) efter ett verifieringsmail.';

  if (msg.includes('user already registered') || msg.includes('already been registered'))
    return 'Det finns redan ett konto med den e-postadressen. Logga in istället, eller använd "Glömt lösenord?".';

  if (msg.includes('password should be at least'))
    return 'Lösenordet måste vara minst 6 tecken.';

  if (msg.includes('unable to validate email') || msg.includes('invalid email'))
    return 'Kontrollera att e-postadressen är korrekt skriven.';

  if (msg.includes('network') || msg.includes('fetch') || msg.includes('failed to fetch'))
    return 'Kunde inte nå servern. Kontrollera din internetanslutning och försök igen.';

  if (msg.includes('for security purposes') || msg.includes('after') || msg.includes('seconds'))
    return 'Du måste vänta lite innan du kan försöka igen. Prova om någon minut.';

  if (msg.includes('weak password'))
    return 'Välj ett starkare lösenord (minst 6 tecken, gärna med siffror).';

  // Fallback – dölj råtexten, ge generellt lugnande besked
  return 'Något gick fel. Vänta en stund och försök igen, eller kontakta din terapeut.';
}

// ─── Design tokens för login-skalet ──────────────────────────────────────────
const login = {
  pageBg: NEUTRAL.ink,
  cardBg: NEUTRAL.card,
  accent: KLINIO_DEFAULT_ACCENT,
  accentHover: '#1F3A2D',
  inputBorder: NEUTRAL.line,
  inputBorderFocus: KLINIO_DEFAULT_ACCENT,
  textPrimary: NEUTRAL.ink,
  textMuted: NEUTRAL.muted,
  textFaint: NEUTRAL.faint,
  divider: NEUTRAL.line,
  errorText: STATUS_COLORS.error,
  successText: STATUS_COLORS.success,
};

const inputStyle = {
  padding: '12px 15px',
  borderRadius: '9px',
  border: `1px solid ${login.inputBorder}`,
  fontSize: '0.94em',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  color: login.textPrimary,
  background: NEUTRAL.page,
};

const primaryBtn = (disabled, accentColor) => ({
  padding: '12px',
  background: disabled ? NEUTRAL.faint : (accentColor || login.accent),
  color: disabled ? 'white' : onAccent(accentColor || login.accent),
  border: 'none',
  borderRadius: '9px',
  cursor: disabled ? 'default' : 'pointer',
  fontSize: '0.94em',
  fontWeight: '700',
  letterSpacing: '0.01em',
  opacity: disabled ? 0.75 : 1,
});


// ─── Login-skal (T-12) ───────────────────────────────────────────────────────
// Med varumärke (kund på klinikens adress / ?t=-länk): delad vy – mörk
// varumärkespanel + vitt formulär (staplas på mobil). Utan varumärke
// (terapeut på plattformens adress): vitt kort på ljus sida med Klinikverktygets märke.
// Blandar en hexfärg mot vitt (t = andel vitt). Används för ikonfärg på mörk panel.
function tintHex(hex, t) {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return '#FFFFFF';
  const n = parseInt(m[1], 16);
  const ch = (v) => Math.round(v + (255 - v) * t).toString(16).padStart(2, '0');
  return `#${ch((n >> 16) & 255)}${ch((n >> 8) & 255)}${ch(n & 255)}`;
}

const LOGIN_FEATURES = [
  { label: 'Boka och avboka tider själv', path: <><rect x="3.5" y="5" width="17" height="15" rx="2.5" /><path d="M3.5 10h17M8 3v4M16 3v4" /></> },
  { label: 'Följ ditt träningsprogram', path: <path d="M3 12h4l3-7 4 14 3-7h4" /> },
  { label: 'Skriv direkt till din terapeut', path: <path d="M4 5.5h16v10H9l-5 4z" /> },
];

function LoginShell({ brand, title, sub, children }) {
  const hasBrand = brand && brand.displayName;
  const accent = (hasBrand && brand.accentColor) || login.accent;
  // Ljus logga (logoDarkUrl) direkt på mörk panel; annars vanliga loggan på vit platta. Gäller dator och mobil.
  // Bred logga (ordmärke) bär själv namn/slogan → visas utan text; symbol-logga får namnet bredvid.
  const [darkFailed, setDarkFailed] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [logoIsWordmark, setLogoIsWordmark] = useState(true);
  const onLogoLoad = (e) => {
    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
    if (w && h) setLogoIsWordmark(w / h >= 1.8);
  };

  if (hasBrand) {
    const useDark = !!brand.logoDarkUrl && !darkFailed;
    const usePlate = !useDark && !!brand.logoUrl && !logoFailed;
    const hasLogo = useDark || usePlate;
    const showText = !hasLogo || !logoIsWordmark;
    const iconColor = tintHex(accent, 0.45);
    return (
      <div className="fr-login-split" style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: '5fr 6fr', background: NEUTRAL.card }}>
        <div className="fr-login-panel" style={{ position: 'relative', overflow: 'hidden', background: NEUTRAL.ink, color: '#FFFFFF', padding: '64px 56px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '32px' }}>
          <div aria-hidden="true" style={{ position: 'absolute', top: '-140px', left: '-100px', width: '460px', height: '460px', borderRadius: '999px', background: accent, opacity: 0.28, filter: 'blur(60px)', pointerEvents: 'none' }} />
          <div aria-hidden="true" style={{ position: 'absolute', bottom: '-180px', right: '-80px', width: '360px', height: '360px', borderRadius: '999px', background: accent, opacity: 0.16, filter: 'blur(60px)', pointerEvents: 'none' }} />

          {/* Varumärkesgrupp – samma logik på dator och mobil (mobil: centrerad, mindre, utan text/punkter) */}
          <div className="fr-login-brand" style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '28px', width: '100%', maxWidth: '440px' }}>
            {useDark && (
              <img className="fr-login-darklogo" src={brand.logoDarkUrl} alt={brand.displayName} onLoad={onLogoLoad} onError={() => setDarkFailed(true)}
                style={{ display: 'block', width: 'auto', maxWidth: 'min(340px, 100%)', height: 'auto', maxHeight: '150px' }} />
            )}
            {usePlate && (
              <div className="fr-login-plate" style={{ alignSelf: 'flex-start', background: '#FFFFFF', borderRadius: '20px', padding: '24px 32px', boxShadow: '0 20px 50px rgba(0,0,0,0.35)', maxWidth: '100%', boxSizing: 'border-box' }}>
                <img className="fr-login-plateimg" src={brand.logoUrl} alt={brand.displayName} onLoad={onLogoLoad} onError={() => setLogoFailed(true)}
                  style={{ display: 'block', width: 'auto', maxWidth: 'min(280px, 100%)', height: 'auto', maxHeight: '120px' }} />
              </div>
            )}
            {showText && (
              <div className="fr-login-text">
                <h1 className="fr-login-name" style={{ margin: 0, fontSize: hasLogo ? '28px' : '44px', fontWeight: '800', lineHeight: 1.1, letterSpacing: '-0.015em', fontFamily: 'var(--font-heading)', color: '#FFFFFF' }}>{brand.displayName}</h1>
                {brand.tagline && <div className="fr-login-tag" style={{ fontSize: '18px', color: 'rgba(255,255,255,0.8)', marginTop: '10px', lineHeight: 1.4 }}>{brand.tagline}</div>}
              </div>
            )}
            <div className="fr-login-extra" aria-hidden="true" style={{ width: '48px', height: '3px', borderRadius: '3px', background: accent }} />
            <p className="fr-login-extra" style={{ margin: 0, fontSize: '18px', lineHeight: 1.6, color: 'rgba(255,255,255,0.8)' }}>
              Boka tid, följ ditt program och håll kontakten med din terapeut – allt på ett ställe.
            </p>
            <ul className="fr-login-extra" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {LOGIN_FEATURES.map(f => (
                <li key={f.label} style={{ display: 'flex', alignItems: 'center', gap: '14px', fontSize: '15px', color: 'rgba(255,255,255,0.86)' }}>
                  <span style={{ width: '38px', height: '38px', borderRadius: '11px', background: `${accent}29`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">{f.path}</svg>
                  </span>
                  {f.label}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="fr-login-form" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
          <div style={{ width: '100%', maxWidth: '360px' }}>
            <div style={{ marginBottom: '18px' }}>
              <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: NEUTRAL.ink, fontFamily: 'var(--font-heading)' }}>{title}</h2>
              {sub && <div style={{ fontSize: '13px', color: NEUTRAL.muted, marginTop: '4px', lineHeight: 1.5 }}>{sub}</div>}
            </div>
            {children}
            {brand.privacyPolicyUrl && (
              <div style={{ fontSize: '11px', color: NEUTRAL.faint, textAlign: 'center', marginTop: '20px', lineHeight: 1.5 }}>
                Genom att logga in godkänner du <a href={brand.privacyPolicyUrl} target="_blank" rel="noopener noreferrer" style={{ color: NEUTRAL.muted }}>integritetspolicyn</a>.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: NEUTRAL.page, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' }}>
      <div style={{ background: NEUTRAL.card, border: `1px solid ${NEUTRAL.line}`, borderRadius: '14px', boxShadow: '0 1px 3px rgba(10,7,6,0.04)', padding: '28px', width: '100%', maxWidth: '380px', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
          <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: NEUTRAL.ink, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '3px', background: login.accent }} />
          </div>
          <span style={{ fontSize: '14px', fontWeight: '700', color: NEUTRAL.ink }}>Klinikverktyget</span>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: NEUTRAL.ink, fontFamily: 'var(--font-heading)' }}>{title}</h1>
          {sub && <div style={{ fontSize: '13px', color: NEUTRAL.muted, marginTop: '4px', lineHeight: 1.5 }}>{sub}</div>}
        </div>
        {children}
        <div style={{ fontSize: '12px', color: NEUTRAL.faint, textAlign: 'center', lineHeight: 1.5, borderTop: `1px solid ${NEUTRAL.line}`, paddingTop: '12px', marginTop: '16px' }}>
          Är du kund hos en klinik? Logga in via klinikens egen adress eller länken du fått.
        </div>
      </div>
    </div>
  );
}

function App() {
  const [session, setSession] = useState(null);
  const [role, setRole] = useState(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingRole, setCheckingRole] = useState(false);
  const [message, setMessage] = useState('');
  const [view, setView] = useState('login');
  const [signupDone, setSignupDone] = useState(false);
  // Startskydd: visa ingen inloggningsvy förrän sessionen är läst och – på en kliniks adress
  // eller ?t=-länk – klinikens varumärke är hämtat. Annars syns plattformens inloggning en stund.
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [brandResolved, setBrandResolved] = useState(
    () => isPlatformHost() && !new URLSearchParams(window.location.search).get('t')
  );
  const [isRecovery, setIsRecovery] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [brand, setBrand] = useState(null); // { displayName, logoUrl, accentColor, tagline }

  const navigateTo = (path) => {
    window.history.pushState({}, '', path);
    setCurrentPath(path);
  };

  // Ny patient – aktiveringsflöde
  const [setupTherapistId, setSetupTherapistId] = useState(null);
  const [setupName, setSetupName] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  // T-9: egen adress per terapeut. På t.ex. boka.fasciarehab.se slås varumärket upp på
  // värdnamnet (publik RPC utan PII) och terapeuten blir förvald precis som via ?t=.
  // ?t= i URL:en vinner om båda finns (hanteras i effekten nedan som körs efter).
  useEffect(() => {
    if (isPlatformHost()) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc('get_public_brand', { p_host: window.location.hostname });
      const row = Array.isArray(data) ? data[0] : data;
      if (!cancelled && !row?.therapist_id) setBrandResolved(true);
      if (!cancelled && row?.therapist_id) {
        if (!new URLSearchParams(window.location.search).get('t')) {
          localStorage.setItem('fr_pending_therapist', row.therapist_id);
        }
        const b = {
          displayName: row.display_name || '',
          logoUrl: row.logo_url || '',
          iconUrl: row.icon_url || '',
          accentColor: row.accent_color || login.accent,
          tagline: row.tagline || '',
          privacyPolicyUrl: row.privacy_policy_url || '',
          logoDarkUrl: '',
        };
        // Ljus logga för mörk bakgrund läses direkt ur brand_settings (anon-läsning finns redan).
        const { data: extra } = await supabase.from('brand_settings').select('logo_dark_url').eq('therapist_id', row.therapist_id).maybeSingle();
        if (cancelled) return;
        b.logoDarkUrl = extra?.logo_dark_url || '';
        setBrand(b);
        applyBrandToDocument(b);
        setBrandResolved(true);
      }
    })().catch(() => { if (!cancelled) setBrandResolved(true); });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Läs ?t=<therapist_id> ur URL och spara i localStorage
  // localStorage överlever email-verifiering, redirect och flikbyte
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('t');
    if (t) {
      localStorage.setItem('fr_pending_therapist', t);
      lookupBrandByTherapistId(t).finally(() => setBrandResolved(true));
    }
    // T-3: djuplänk från hemsidan med förvald tjänst/dag/tid. Endast formatkontroll här –
    // värdena valideras mot terapeutens riktiga data i ClientView innan de används.
    const service = params.get('service');
    const date = params.get('date');
    const time = params.get('time');
    const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (service && uuidRe.test(service)) {
      const pending = { service };
      if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) pending.date = date;
      if (pending.date && time && /^\d{2}:\d{2}$/.test(time)) pending.time = time;
      localStorage.setItem('fr_pending_booking', JSON.stringify(pending));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync currentPath with browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => setCurrentPath(window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const lookupBrandByTherapistId = async (therapistId) => {
    const { data: brandData } = await supabase
      .from('brand_settings')
      .select('display_name, logo_url, logo_dark_url, icon_url, accent_color, tagline, privacy_policy_url')
      .eq('therapist_id', therapistId)
      .maybeSingle();

    if (brandData?.display_name) {
      const b = {
        displayName: brandData.display_name || '',
        logoUrl: brandData.logo_url || '',
        iconUrl: brandData.icon_url || '',
        accentColor: brandData.accent_color || login.accent,
        tagline: brandData.tagline || '',
        privacyPolicyUrl: brandData.privacy_policy_url || '',
        logoDarkUrl: brandData.logo_dark_url || '',
      };
      setBrand(b);
      applyBrandToDocument(b);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) checkRole(session);
    }).finally(() => setSessionLoaded(true));

    // Säkerhetsnät: om varumärket inte hunnit hämtas på 4 s visas sidan ändå.
    const brandTimeout = setTimeout(() => setBrandResolved(true), 4000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        if (event === 'PASSWORD_RECOVERY') {
          setIsRecovery(true);
        }
        if (session) checkRole(session);
        else { setRole(null); setIsPlatformAdmin(false); }
      }
    );

    return () => { subscription.unsubscribe(); clearTimeout(brandTimeout); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkRole = async (session) => {
    setCheckingRole(true);
    setIsPlatformAdmin(false);

    // Kontrollera terapeut-roll och platform_admin-roll parallellt
    const [{ data: therapist }, { data: isAdmin }] = await Promise.all([
      supabase.from('therapists').select('id').eq('user_id', session.user.id).maybeSingle(),
      supabase.rpc('is_platform_admin'),
    ]);

    if (isAdmin === true) setIsPlatformAdmin(true);

    if (therapist) {
      setRole('therapist');
      setCheckingRole(false);
      return;
    }

    // Kontrollera om det finns en clients-rad kopplad till denna e-post
    const { data: clientRecord } = await supabase
      .from('clients')
      .select('id')
      .ilike('email', session.user.email.toLowerCase().trim())
      .maybeSingle();

    if (clientRecord) {
      // Befintlig patient – in i appen
      setRole('client');
      setCheckingRole(false);
      return;
    }

    // Ny patient utan clients-rad – kolla om det finns en väntande terapeutkoppling.
    // Läs även direkt från URL som fallback (RLS blockerar pre-validering mot therapists-tabellen
    // för klientanvändare, och skyddar mot timing-race mellan URL-effekt och checkRole).
    let pendingId =
      localStorage.getItem('fr_pending_therapist') ||
      new URLSearchParams(window.location.search).get('t');

    // T-9: på en terapeuts egen adress räcker adressen som koppling (skyddar mot
    // timing-race mellan värdnamnsuppslaget och checkRole).
    if (!pendingId && !isPlatformHost()) {
      const { data } = await supabase.rpc('get_public_brand', { p_host: window.location.hostname });
      const row = Array.isArray(data) ? data[0] : data;
      if (row?.therapist_id) pendingId = row.therapist_id;
    }

    if (pendingId) {
      localStorage.setItem('fr_pending_therapist', pendingId);
      lookupBrandByTherapistId(pendingId);
      setSetupTherapistId(pendingId);
    } else {
      // Konto finns men ingen koppling – visa tom klientvy
      setRole('client');
    }

    setCheckingRole(false);
  };

  // Aktiverar nytt patientkonto: skapar clients-rad och kopplar till terapeut
  const handleActivateAccount = async (e) => {
    e.preventDefault();
    if (!setupName.trim() || !privacyAccepted) return;
    setLoading(true);
    setMessage('');

    const { error } = await supabase.from('clients').insert({
      therapist_id: setupTherapistId,
      name: setupName.trim(),
      email: session.user.email.toLowerCase().trim(),
      privacy_accepted_at: new Date().toISOString(),
    });

    if (error) {
      if (error.code === '23503') {
        // FK-violation: therapist_id finns inte i therapists-tabellen
        setMessage('Ogiltig terapeutkod. Be din terapeut om en ny länk.');
        localStorage.removeItem('fr_pending_therapist');
        setSetupTherapistId(null);
        setRole('client');
      } else if (error.code === '23505') {
        // clients-rad finns redan – dubbel-insert-skydd
        localStorage.removeItem('fr_pending_therapist');
        setSetupTherapistId(null);
        setRole('client');
      } else {
        setMessage('Något gick fel: ' + error.message);
      }
    } else {
      localStorage.removeItem('fr_pending_therapist');
      setSetupTherapistId(null);
      setRole('client');
    }
    setLoading(false);
  };

  // Försöker hämta terapeutens brand baserat på patientens e-post
  const lookupBrandByEmail = async (emailValue) => {
    if (!emailValue || !emailValue.includes('@')) return;

    const { data: clientData } = await supabase
      .from('clients')
      .select('therapist_id')
      .ilike('email', emailValue.toLowerCase().trim())
      .maybeSingle();

    if (!clientData?.therapist_id) {
      // Behåll brand från ?t= om det finns, annars rensa
      if (!localStorage.getItem('fr_pending_therapist')) setBrand(null);
      return;
    }

    const { data: brandData } = await supabase
      .from('brand_settings')
      .select('display_name, logo_url, logo_dark_url, icon_url, accent_color, tagline')
      .eq('therapist_id', clientData.therapist_id)
      .maybeSingle();

    if (brandData) {
      const b = {
        displayName: brandData.display_name || '',
        logoUrl: brandData.logo_url || '',
        iconUrl: brandData.icon_url || '',
        accentColor: brandData.accent_color || login.accent,
        tagline: brandData.tagline || '',
        logoDarkUrl: brandData.logo_dark_url || '',
      };
      setBrand(b);
      applyBrandToDocument(b);
    } else {
      setBrand(null);
    }
  };

  const handleEmailBlur = () => {
    lookupBrandByEmail(email);
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setMessage(authError(error.message));
    setLoading(false);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setMessage('');
    if (!email.trim() || !password) {
      setMessage('Fyll i din e-post och välj ett lösenord.');
      return;
    }
    setLoading(true);
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // Inkludera ?t= så therapist_id överlever email-verifiering i annan webbläsare
        emailRedirectTo: (() => {
          const pending = localStorage.getItem('fr_pending_therapist');
          if (!pending) return `${window.location.origin}/`;
          // T-3: ta med förvald tjänst/dag/tid så djuplänken överlever verifiering i annan webbläsare
          let extra = '';
          try {
            const b = JSON.parse(localStorage.getItem('fr_pending_booking') || 'null');
            if (b?.service) {
              extra = `&service=${encodeURIComponent(b.service)}`;
              if (b.date) extra += `&date=${encodeURIComponent(b.date)}`;
              if (b.time) extra += `&time=${encodeURIComponent(b.time)}`;
            }
          } catch (_) { /* ogiltig lagrad bokning ignoreras */ }
          return `${window.location.origin}/?t=${pending}${extra}`;
        })(),
        // Klinikens namn till verifieringsmejlets mall ({{ .Data.clinic_name }})
        data: { clinic_name: brand?.displayName || '' },
      },
    });
    if (error) {
      setMessage(authError(error.message));
    } else if (data?.user?.identities?.length === 0) {
      // Supabase returnerar tom identities när e-posten redan är registrerad.
      // Inget nytt verifieringsmail skickas i det fallet.
      setMessage('Det finns redan ett konto med den e-postadressen. Logga in istället, eller använd "Glömt lösenord?".');
    } else {
      setSignupDone(true);
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/`,
    });
    if (error) {
      setMessage(authError(error.message));
    } else {
      setMessage('Återställningslänk skickad! Kolla din e-post – och skräppostmappen om inget syns inom en minut.');
    }
    setLoading(false);
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      setMessage(authError(error.message));
    } else {
      setMessage('Lösenord ändrat!');
      setIsRecovery(false);
      setNewPassword('');
    }
    setLoading(false);
  };

  const accentColor = brand?.accentColor || login.accent;

  if (!sessionLoaded || !brandResolved) return (
    <div style={{ background: login.pageBg, minHeight: '100vh' }} aria-busy="true" />
  );

  // ─── Lösenordsåterställning ────────────────────────────────────────────────
  if (isRecovery) {
    return (
      <LoginShell brand={brand} title="Nytt lösenord" sub="Välj ett nytt lösenord för ditt konto">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <input
            type="password"
            placeholder="Nytt lösenord (minst 6 tecken)"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            style={inputStyle}
          />
          <button onClick={handleUpdatePassword} disabled={loading} style={primaryBtn(loading, accentColor)}>
            {loading ? 'Sparar...' : 'Spara nytt lösenord'}
          </button>
        </div>
        {message && (
          <p style={{ marginTop: '16px', color: login.successText, fontSize: '0.875em', textAlign: 'center' }}>
            {message}
          </p>
        )}
      </LoginShell>
    );
  }

  if (session && checkingRole) return (
    <div style={{ background: login.pageBg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.9em' }}>Laddar...</p>
    </div>
  );

  // ─── Ny patient – aktiveringsvy ────────────────────────────────────────────
  // Visas när en ny patient kommit via ?t=-länk, skapat konto och verifierat email,
  // men ännu inte har en clients-rad i databasen.
  if (session && setupTherapistId) {
    return (
      <LoginShell brand={brand} title="Välkommen!" sub="Nästan klart – vi behöver bara ditt namn.">
        <form onSubmit={handleActivateAccount} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <input
            type="text"
            placeholder="Ditt namn"
            value={setupName}
            onChange={(e) => setSetupName(e.target.value)}
            autoFocus
            required
            style={inputStyle}
          />
          <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '0.85em', color: login.textMuted, lineHeight: '1.45', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={privacyAccepted}
              onChange={(e) => setPrivacyAccepted(e.target.checked)}
              style={{ marginTop: '3px', flexShrink: 0 }}
            />
            <span>
              Jag har tagit del av informationen om hur {brand?.displayName || 'min terapeut'} behandlar
              mina personuppgifter (namn, kontaktuppgifter och bokningar) i bokningssystemet.
              {brand?.privacyPolicyUrl && (
                <> <a href={brand.privacyPolicyUrl} target="_blank" rel="noopener noreferrer" style={{ color: login.textPrimary, fontWeight: '600' }}>Läs integritetspolicyn</a>.</>
              )}
            </span>
          </label>
          <button
            type="submit"
            disabled={loading || !setupName.trim() || !privacyAccepted}
            style={primaryBtn(loading || !setupName.trim() || !privacyAccepted, accentColor)}
          >
            {loading ? 'Aktiverar...' : 'Aktivera mitt konto'}
          </button>
        </form>
        {message && (
          <p style={{ marginTop: '16px', color: login.errorText, fontSize: '0.875em', textAlign: 'center' }}>
            {message}
          </p>
        )}
      </LoginShell>
    );
  }

  // ─── /owner route ──────────────────────────────────────────────────────────
  if (session && !checkingRole && !setupTherapistId && currentPath === '/owner') {
    if (isPlatformAdmin) {
      return (
        <OwnerDashboard
          session={session}
          isTherapist={role === 'therapist'}
          onNavigateBack={() => navigateTo('/')}
        />
      );
    }
    return (
      <div style={{ background: login.pageBg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.9em' }}>Ej behörig.</p>
      </div>
    );
  }

  if (session && role === 'therapist') return <Dashboard session={session} isPlatformAdmin={isPlatformAdmin} onNavigateToOwner={() => navigateTo('/owner')} />;
  if (session && role === 'client') return <ClientView session={session} />;

  // ─── Glömt lösenord ────────────────────────────────────────────────────────
  if (view === 'reset') {
    return (
      <LoginShell brand={brand} title="Glömt lösenord?" sub="Ange din e-post så skickar vi en återställningslänk">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <input
            type="email"
            placeholder="E-post"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={handleEmailBlur}
            style={inputStyle}
          />
          <button onClick={handleResetPassword} disabled={loading} style={primaryBtn(loading, accentColor)}>
            {loading ? 'Skickar...' : 'Skicka återställningslänk'}
          </button>
          <button
            onClick={() => { setView('login'); setMessage(''); }}
            type="button"
            style={{ padding: '10px', background: 'transparent', color: login.textMuted, border: 'none', cursor: 'pointer', fontSize: '0.875em' }}
          >
            ← Tillbaka till inloggning
          </button>
        </div>
        {message && (
          <p style={{ marginTop: '16px', color: login.successText, fontSize: '0.875em', textAlign: 'center' }}>
            {message}
          </p>
        )}
      </LoginShell>
    );
  }

  // ─── Skapa konto ───────────────────────────────────────────────────────────
  if (view === 'signup') {
    const backToLogin = () => { setView('login'); setMessage(''); setSignupDone(false); };
    if (signupDone) {
      return (
        <LoginShell brand={brand} title="Kolla din e-post" sub={`Vi har skickat en länk till ${email}`}>
          <p style={{ color: login.textMuted, fontSize: '0.95em', lineHeight: 1.5, margin: '0 0 16px' }}>
            Klicka på länken i mejlet för att aktivera ditt konto. Hittar du det inte inom
            någon minut – kolla skräppostmappen.
          </p>
          <button onClick={backToLogin} type="button" style={{ padding: '10px', background: 'transparent', color: login.textMuted, border: 'none', cursor: 'pointer', fontSize: '0.875em', fontFamily: 'inherit' }}>
            ← Tillbaka till inloggning
          </button>
        </LoginShell>
      );
    }
    return (
      <LoginShell
        brand={brand}
        title="Skapa konto"
        sub={brand?.displayName ? `Bli kund hos ${brand.displayName}` : 'Ange din e-post och välj ett lösenord'}
      >
        <form onSubmit={handleSignUp} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <input
            type="email"
            placeholder="E-post"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
          />
          <input
            type="password"
            placeholder="Välj lösenord (minst 6 tecken)"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={inputStyle}
          />
          <button type="submit" disabled={loading} style={{ ...primaryBtn(loading, accentColor), marginTop: '4px' }}>
            {loading ? 'Skapar konto...' : 'Skapa konto'}
          </button>
          <div style={{ textAlign: 'center', fontSize: '0.875em', color: login.textMuted, marginTop: '6px' }}>
            Har du redan ett konto?{' '}
            <button onClick={backToLogin} type="button" style={{ background: 'none', border: 'none', color: login.textPrimary, fontWeight: '600', cursor: 'pointer', fontSize: '1em', padding: 0, fontFamily: 'inherit' }}>Logga in</button>
          </div>
        </form>
        {message && (
          <p style={{ marginTop: '16px', color: login.errorText, fontSize: '0.875em', textAlign: 'center' }}>
            {message}
          </p>
        )}
      </LoginShell>
    );
  }

  // ─── Inloggning ────────────────────────────────────────────────────────────
  return (
    <LoginShell brand={brand} title="Logga in" sub={brand?.displayName ? 'Välkommen tillbaka' : 'För dig som är terapeut'}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <input
          type="email"
          placeholder="E-post"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={handleEmailBlur}
          style={inputStyle}
        />
        <input
          type="password"
          placeholder="Lösenord"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={inputStyle}
        />
        <button onClick={handleSignIn} disabled={loading} style={{ ...primaryBtn(loading, accentColor), marginTop: '4px' }}>
          {loading ? 'Loggar in...' : 'Logga in'}
        </button>
        <button
          onClick={() => { setView('reset'); setMessage(''); }}
          type="button"
          style={{ padding: '6px', background: 'transparent', color: login.textMuted, border: 'none', cursor: 'pointer', fontSize: '0.875em', fontFamily: 'inherit' }}
        >
          Glömt lösenord?
        </button>
        <div style={{ textAlign: 'center', fontSize: '0.875em', color: login.textMuted, marginTop: '6px' }}>
          {brand?.displayName ? 'Ny kund?' : 'Ny här?'}{' '}
          <button onClick={() => { setView('signup'); setMessage(''); setPassword(''); }} type="button" style={{ background: 'none', border: 'none', color: login.textPrimary, fontWeight: '600', cursor: 'pointer', fontSize: '1em', padding: 0, fontFamily: 'inherit' }}>Skapa konto</button>
        </div>
      </div>
      {message && (
        <p style={{ marginTop: '16px', color: login.errorText, fontSize: '0.875em', textAlign: 'center' }}>
          {message}
        </p>
      )}
    </LoginShell>
  );
}

export default App;
